import { ChangeEvent, KeyboardEventHandler, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';

import { ReceptionistListItem } from './_authenticated.receptionists/ReceptionistListItem';
import ConfusedImage from '../assets/images/confused.png';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { Spinner } from '../components/Spinner';
import { useFetchUser } from '../hooks/useFetchUser';
import { useUpdateInvoiceMention } from '../hooks/useUpdateInvoiceMention';
import { useReceptionists } from '../services/receptionists/useReceptionists';
import { RECEPTIONISTS_INVOICE_MENTION_DATA } from '../services/receptionists/utils';
import { t } from '../services/translation/translation';

export const ReceptionistsRoute = () => {
  const { data: user } = useFetchUser();
  const { receptionists, activeReceptionist, isFetched, isLoading, setActiveReceptionist } = useReceptionists();
  const [receptionistToAdd, setReceptionistToAdd] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutate: updateInvoiceMention, context, isLoading: isUpdatingInvoiceMention } = useUpdateInvoiceMention();

  const handleAdd = () => {
    if (!isFetched) return;

    if (!receptionistToAdd) {
      setErrorMessage(t('common.field-required'));
      return;
    }

    if (receptionists.includes(receptionistToAdd.toUpperCase())) {
      setErrorMessage(t('receptionists-route.duplicate-error'));
      return;
    }

    const newList = [...receptionists];
    newList.push(receptionistToAdd);
    updateInvoiceMention({ ...RECEPTIONISTS_INVOICE_MENTION_DATA, values: newList });
    setReceptionistToAdd('');
  };

  const handleUpdate = useCallback(
    (receptionistToUpdate: string, newReceptionist: string) => {
      if (isFetched && receptionists.includes(receptionistToUpdate)) {
        updateInvoiceMention(
          {
            ...RECEPTIONISTS_INVOICE_MENTION_DATA,
            values: receptionists.map(v => (v === receptionistToUpdate ? newReceptionist : v)),
            receptionistToUpdate,
          },
          {
            onSuccess: () => {
              if (receptionistToUpdate === activeReceptionist) {
                setActiveReceptionist(newReceptionist);
              }
            },
          },
        );
      }
    },
    [activeReceptionist, isFetched, receptionists, setActiveReceptionist, updateInvoiceMention],
  );

  const handleDelete = useCallback(
    (receptionistToDelete: string) => {
      if (isFetched && receptionists.includes(receptionistToDelete)) {
        updateInvoiceMention(
          {
            ...RECEPTIONISTS_INVOICE_MENTION_DATA,
            values: receptionists.filter(v => v !== receptionistToDelete),
            receptionistToDelete,
          },
          {
            onSuccess: () => {
              if (receptionistToDelete === activeReceptionist) {
                setActiveReceptionist(null);
              }
            },
          },
        );
      }
    },
    [activeReceptionist, isFetched, receptionists, setActiveReceptionist, updateInvoiceMention],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setReceptionistToAdd(e.target.value);
    setErrorMessage(null);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      handleAdd();
    }
  };

  if (!user) {
    return (
      <div className="py-4">
        <Spinner />
      </div>
    );
  }

  if (!user.admin) {
    return <Navigate to="/" />;
  }

  return (
    <div className="p-4">
      <Helmet>
        <title>{t('receptionists-route.meta-title')}</title>
      </Helmet>
      <h1 className="text-2xl">{t('receptionists-route.title')}</h1>

      <div className="my-2 flex gap-x-2 items-start">
        <div className="flex-1">
          <input
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            placeholder="Pam Beesly"
            value={receptionistToAdd}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          {errorMessage ? (
            <p className="mt-2 text-sm text-error" id="email-error">
              {errorMessage}
            </p>
          ) : null}
        </div>
        {isUpdatingInvoiceMention && !context?.receptionistToDelete ? <Spinner /> : null}
        <PrimaryButton onClick={handleAdd} text={t('common.add')} />
      </div>

      {isLoading ? <Spinner /> : null}

      {receptionists.map((receptionist, index) => (
        <ReceptionistListItem
          receptionist={receptionist}
          even={index % 2 === 0}
          isDeleting={isUpdatingInvoiceMention && context?.receptionistToDelete === receptionist}
          isUpdating={isUpdatingInvoiceMention && context?.receptionistToUpdate === receptionist}
          key={receptionist}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      ))}

      {receptionists.length === 0 && isFetched ? (
        <div className="flex flex-col items-center gap-y-2 mt-4">
          <img className="w-52" src={ConfusedImage} alt="Personnage confus" />
          <p className="font-bold">{t('receptionists-route.empty')}</p>
        </div>
      ) : null}
    </div>
  );
};
